import React, {Fragment, useContext, useState} from 'react';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {StandardTypography} from 'patient-ping-remedy/packages/typography';
import Popover from 'patient-ping-remedy/packages/popover';
import {useAuthStore} from '@/store/auth_store';
import {Client, Roster} from '@/api/dto/dto';
import Avatar from '@common/Avatar';
import AssignedToList, {AssignUserParams} from '@common/AssignedToList';
import {Wrapper} from '@clients/AssignedToClient.styles';
import AvatarDetails from "@common/AvatarDetails";
import {QueryKey, useMutation, useQueryClient} from "@tanstack/react-query";
import {AssignmentCache, applyAssignmentToCache} from "@/helpers/assignment_cache";
import {log} from "@/helpers/logger";
import {MixpanelEventType} from "@/api/dto/mixpanel";
import {AxiosError} from "axios";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import Helpers from "@/helpers/helpers";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {useMixpanel} from "@/hooks/useMixpanel";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import {FeatureToggle} from "@/api/dto/dto";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {useOptimisticUpdate} from "@/hooks/useOptimisticUpdate";

type Props = {
  client: Client;
  roster?: Roster | null;
  maxCount?: number;
  // Cache key this component's assignment mutation snapshots, rolls back, and
  // invalidates. Defaults to the clients list. Screens that render this component
  // against a different query (e.g. the CRF reports) must pass their own key,
  // otherwise a successful assign never refetches and the row stays stale.
  invalidateQueryKey?: QueryKey;
}

export default function AssignedToClient(props: Props) {
  const { maxCount = 2 } = props;
  const { currentUser} = useAuthStore();
  const {carecoApi} = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const [assignedListOpen, setAssignedListOpen] = useState<boolean>(false);
  const maxCountWithoutSelf = maxCount - 1;
  const {mixpanelEventProperties} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const { mutateAsync: assignUser } = useMutation(useOptimisticUpdate<
    AssignmentCache,
    AssignUserParams
  >({
    mutationFn: async (params: AssignUserParams) => {
      return params.delete
        ? carecoApi?.deleteClientUserAssignment(props.client.patientId, params.user.id, props.roster!.id)
        : carecoApi?.postClientUserAssignment(props.client.patientId, params.user.id, props.roster!.id);
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['assignedToFilterOptions', props.roster?.id] })
        .then(r => log('info', r));

      if (currentUser && !params.delete && params.user.id === currentUser.id && mixpanelEventProperties) {
        carecoApi?.postMixpanelEvent({
          eventName: MixpanelEventType.BIH_ASSIGNED_CLIENT_TO_SELF,
          ...mixpanelEventProperties
        });
      }
    },
    onError: (error: AxiosError, params) => {
      Helpers.alertOnError(params.delete ? `Failed to unassign user from client. ${GENERICERROR} ${Helpers.traceId(error)}` :
        `Failed to assign user to client. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
    optimisticUpdateQueryKey: props.invalidateQueryKey ?? ['clients'],
    onOptimisticUpdate(cache, params) {
      return applyAssignmentToCache(cache, props.client.patientId, (current) =>
        params.delete
          ? current.filter((userAssignment) => userAssignment.user.id !== params.user.id)
          : [...current, { user: params.user }]);
    },
  }));

  const userAssignmentsWithoutSelf = props.client.userAssignments
    .filter((userAssignment) => userAssignment.user.id !== currentUser?.id);

  const assignedToSelf = userAssignmentsWithoutSelf.length !== props.client.userAssignments.length;

  const content = (
    <Wrapper>
      {assignedToSelf ?
        <AvatarDetails user={currentUser} roster={props.roster} />
        : <Avatar iconClass={ICONS['user-plus']} />}
      {userAssignmentsWithoutSelf
        .sort((userAssignment) => userAssignment.user.id === currentUser?.id ? -1 : 0)
        .map((userAssignment, index) => (
          <Fragment key={index}>
            {index < maxCountWithoutSelf &&
                      <AvatarDetails user={userAssignment.user} roster={props.roster} />
            }
            {index === maxCountWithoutSelf && (
              <StandardTypography>+{userAssignmentsWithoutSelf.length - maxCountWithoutSelf}</StandardTypography>
            )}
          </Fragment>
        ))
      }
    </Wrapper>
  );

  const canActOnClientListForRoster = isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser!,
      props.roster!,
      FeaturePermissionLevel.ACT,
      FeatureNew.CLIENT_LIST,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser!,
      props.roster!,
      FeaturePermissionLevel.ACT,
      Feature.CLIENT_LIST,
    );

  const canManageOnClientListForRoster = isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser!,
      props.roster!,
      FeaturePermissionLevel.MANAGE,
      FeatureNew.CLIENT_LIST,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser!,
      props.roster!,
      FeaturePermissionLevel.MANAGE,
      Feature.CLIENT_LIST,
    );

  return (
    <Popover
      isOpen={assignedListOpen}
      toggle={() => setAssignedListOpen(!assignedListOpen)}
      position="bottom-start"
      content={
        <AssignedToList
          assignUser={assignUser}
          roster={props.roster}
          hidePopover={() => setAssignedListOpen(false)}
          assignedUsers={props.client.userAssignments.map(userAssignment => userAssignment.user)}
          canActOnListForRoster={canActOnClientListForRoster}
          canManageOnListForRoster={canManageOnClientListForRoster}
          isTask={false}
        />
      }
    >
      {content}
    </Popover>
  );
}
